import { DateTime } from "luxon";
import { CurationMatchMode } from "./src/types.js";
// Helper function that handles quotes and negative tokens
function tokenizeSearch(search) {
    return search.split(',').map(term => term.trim()).flatMap(term => {
        var _a;
        return ((_a = term.match(/-?"[^"]+"|\S+/g)) === null || _a === void 0 ? void 0 : _a.map((s) => s.startsWith('-"') ? '-' + s.slice(2, -1) : s.replace(/"/g, ''))) || [];
    });
}
// Helper function to check if any field matches one positive token
const matchesPositiveToken = (rowFields, token) => {
    return rowFields.some(fieldValue => fieldValue.includes(token));
};
// Helper function to check if the any field matches all positive tokens
const matchesAllPositiveTokens = (rowFields, searchTokens) => {
    const positiveTokens = searchTokens.filter(token => !token.startsWith('-'));
    return positiveTokens.every(token => matchesPositiveToken(rowFields, token));
};
// Helper function to check if any field matches a negative token
const matchesAnyNegativeToken = (rowFields, searchTokens) => {
    const negativeTokens = searchTokens.filter(token => token.startsWith('-')).map(token => token.substring(1));
    return rowFields.some(fieldValue => negativeTokens.some(token => fieldValue.includes(token)));
};
// const defaultFilter = createFilter({ ignoreAccents: false, ignoreCase:true  });
const customFilterSelectOption = (option, searchString) => {
    const fields = ['displayName', 'vendor', 'productTitle', 'variantTitle', 'description', 'id', 'sku', 'productId'];
    //@ts-ignore
    const rowFields = fields.map(field => option.data[field]);
    return matchSearch(rowFields, searchString);
};
const matchSearch = (rowFields, searchString) => {
    if (!searchString)
        return true;
    const lowercaseRowFields = rowFields.filter(fieldValue => fieldValue !== undefined && fieldValue !== null && fieldValue !== '').map(fieldValue => String(fieldValue).toLowerCase());
    const lowercaseSearchString = searchString.toLowerCase();
    const searchChunks = lowercaseSearchString.split(',').map(chunk => chunk.trim().toLowerCase());
    const matchingChunks = searchChunks.map(chunk => {
        const searchTokens = tokenizeSearch(chunk);
        if (!searchTokens.length)
            return true;
        return matchesAllPositiveTokens(lowercaseRowFields, searchTokens) && !matchesAnyNegativeToken(lowercaseRowFields, searchTokens);
    });
    return matchingChunks.some(match => match); // Union of all results
};
const generateCuration = (products, searchString, value, minimum_inventory, prefer_overstock, inclusions, exclusions, history, box_collections, matchMode, matchModeVariantSearchStrings) => {
    // console.log("Products:", products);
    // console.log("Search String:", searchString);
    // console.log("Value:", value);
    // console.log("Minimum Inventory:", minimum_inventory);
    // console.log("Prefer Overstock:", prefer_overstock);
    // console.log("Inclusions:", inclusions);
    // console.log("Exclusions:", exclusions);
    // console.log("History:", history);
    // console.log("Box Collections:", box_collections);
    // console.log("Match Mode:", matchMode);
    // console.log("Match Mode Variant Search Strings:", matchModeVariantSearchStrings);
    const box_value = value;
    const prefer_recent_or_older = prefer_overstock ? 'older' : 'recent';
    let failReason = null;
    // never automatically curate a product that isn't publicly on sale
    const noPreviewProducts = products.filter(product => !product.onlineStoreUrl.includes('shopifypreview') && !product.tags.includes('Withfriends'));
    if (!noPreviewProducts.length) {
        failReason = "No products at all";
        return failReason;
    }
    const result = noPreviewProducts.map(product => { return Object.assign(Object.assign({}, product), { price: parseFloat(product.price) || 0 }); });
    const min_box_products = 1;
    const max_box_products = 50;
    const number_of_curations = 1;
    const recentCutoff = DateTime.local().minus({ months: 9 });
    const olderCutoff = DateTime.local().minus({ months: 18 });
    const fields = ['productType', 'tags', 'collections', 'productTitle', 'description'];
    const results_matched = result.filter(row => {
        // Prepare row fields for negative comparison
        const rowFields = fields.map(field => { var _a; return typeof row[field] === "object" ? row[field].map((v) => v.toLowerCase()) : (_a = row[field]) === null || _a === void 0 ? void 0 : _a.toLowerCase(); }).flat();
        return (matchMode == CurationMatchMode.Variant || matchSearch(rowFields, searchString)) && (!box_collections.length
            || row['collections'].some((collection) => box_collections.map((bc) => bc.toLowerCase()).includes(collection.toLowerCase()))) && (!(exclusions === null || exclusions === void 0 ? void 0 : exclusions.includes(row.productId.split("/").pop())));
        ;
    });
    if (!results_matched.length) {
        failReason = "No products that match collections, excluded products, and curation search terms";
        return failReason;
    }
    const results_filtered_by_inventory = results_matched.filter(row => {
        return (row.inventory > minimum_inventory || !row.inventoryTracked);
    });
    if (!results_filtered_by_inventory.length) {
        failReason = "No products with inventory";
        return failReason;
    }
    const results_filtered = results_filtered_by_inventory.filter(row => {
        return !(history === null || history === void 0 ? void 0 : history.includes(row.productId.split("/").pop()));
    });
    if (!results_filtered.length) {
        failReason = "No products that aren't duplicates";
        return failReason;
    }
    const unique_results_mapped = {};
    results_filtered.forEach((row) => {
        // @ts-ignore
        if (!unique_results_mapped[row.productId]) {
            // @ts-ignore
            unique_results_mapped[row.productId] = [];
        }
        // @ts-ignore
        unique_results_mapped[row.productId].push(row);
    });
    const unique_results_filtered = results_filtered.filter((row, index, self) => self.findIndex(r => r.productId === row.productId) === index);
    let preferred_results_matched = unique_results_filtered.filter(row => {
        switch (prefer_recent_or_older) {
            case 'recent':
                return DateTime.fromISO(row.createdAt) >= recentCutoff;
            case 'older':
                return DateTime.fromISO(row.createdAt) <= olderCutoff || row.tags.includes('Withfriends Overstock');
        }
    });
    const unpreferred_results_matched = unique_results_filtered.filter(row => {
        switch (prefer_recent_or_older) {
            case 'recent':
                return DateTime.fromISO(row.createdAt) < recentCutoff;
            case 'older':
                return DateTime.fromISO(row.createdAt) > olderCutoff && !row.tags.includes('Withfriends Overstock');
        }
    });
    // preferred_results_matched.sort((a, b) => b.price - a.price);
    preferred_results_matched.sort(() => Math.random() - 0.5);
    const inclusion_results_matched = result.filter(row => inclusions.includes(row.id.split("/").pop()));
    preferred_results_matched = [...inclusion_results_matched, ...preferred_results_matched];
    // @ts-ignore
    const createCurations = (preferred, unpreferred, mapped) => {
        const createdCurations = [];
        // @ts-ignore
        const curation_ids = [];
        if (matchMode == CurationMatchMode.Variant && (matchModeVariantSearchStrings === null || matchModeVariantSearchStrings === void 0 ? void 0 : matchModeVariantSearchStrings.length)) {
            // @ts-ignore
            preferred = preferred.filter(row => {
                var _a;
                // @ts-ignore
                return !!((_a = mapped[row.productId]) === null || _a === void 0 ? void 0 : _a.find(variant => matchSearch([variant.variantTitle], searchString)));
                /*return matchModeVariantSearchStrings.every?.(searchString => {
                  return !searchString || !!mapped[row.productId]?.find(variant => matchSearch([variant.variantTitle], searchString))
                }) || matchModeVariantSearchStrings.every?.(searchString => {
                  return !searchString || !mapped[row.productId]?.find(variant => matchSearch([variant.variantTitle], searchString))
                })*/
            });
            // @ts-ignore
            unpreferred = unpreferred.filter(row => {
                var _a;
                // @ts-ignore
                return !!((_a = mapped[row.productId]) === null || _a === void 0 ? void 0 : _a.find(variant => matchSearch([variant.variantTitle], searchString)));
                /* matchModeVariantSearchStrings.every?.(searchString => {
                  return !searchString || !!mapped[row.productId]?.find(variant => matchSearch([variant.variantTitle], searchString))
                }) || matchModeVariantSearchStrings.every?.(searchString => {
                  return !searchString || !mapped[row.productId]?.find(variant => matchSearch([variant.variantTitle], searchString))
                })*/
            });
        }
        for (let box_products = min_box_products; box_products <= max_box_products; box_products++) {
            let curation_base = [...preferred.slice(0, box_products)];
            let curation_value = curation_base.reduce((sum, product) => sum + product.price, 0);
            while (curation_value > box_value) {
                curation_base.pop();
                curation_value = curation_base.reduce((sum, product) => sum + product.price, 0);
            }
            const remaining_value = box_value - curation_value;
            const remaining_products = box_products - curation_base.length;
            // @ts-ignore
            const unpreferred_results_matched_under_remaining_value = unpreferred.filter(row => row.price <= remaining_value);
            for (let i = 0; i < 10; i++) {
                let curation = [...curation_base];
                for (let j = 0; j < remaining_products && j < unpreferred_results_matched_under_remaining_value.length; j++) {
                    curation.push(unpreferred_results_matched_under_remaining_value[Math.floor(Math.random() * unpreferred_results_matched_under_remaining_value.length)]);
                }
                if (matchMode == CurationMatchMode.Variant) {
                    // Find a variant for this product that matches the option title, if it exists
                    curation = curation.map(product => {
                        var _a, _b;
                        // @ts-ignore
                        return (_b = (_a = mapped[product.productId]) === null || _a === void 0 ? void 0 : _a.find(variant => matchSearch([variant.variantTitle], searchString))) !== null && _b !== void 0 ? _b : product;
                    });
                }
                const ids = curation.map(product => product.id).sort();
                const idsString = JSON.stringify(ids);
                // @ts-ignore
                if (ids.length && !curation_ids.includes(idsString) && ids.length === new Set(ids).size) {
                    const value = curation.reduce((sum, product) => sum + product.price, 0);
                    createdCurations.push({
                        curation,
                        value,
                        closeness: Math.abs(value - box_value),
                    });
                    curation_ids.push(idsString);
                }
            }
        }
        return createdCurations;
    };
    let curations = createCurations(preferred_results_matched, unpreferred_results_matched, unique_results_mapped);
    // Last ditch effort to find a curation including history (but not exclusions)
    if (!curations.length) {
        const results_last_ditch = results_matched.filter(row => {
            return (!row.tags.includes('Withfriends'))
                && (row.inventory > minimum_inventory || !row.inventoryTracked)
                && (!(exclusions === null || exclusions === void 0 ? void 0 : exclusions.includes(row.productId.split("/").pop())));
        });
        const results_last_ditch_mapped = {};
        results_last_ditch.forEach((row) => {
            // @ts-ignore
            if (!results_last_ditch_mapped[row.productId]) {
                // @ts-ignore
                results_last_ditch_mapped[row.productId] = [];
            }
            // @ts-ignore
            results_last_ditch_mapped[row.productId].push(row);
        });
        const results_last_ditch_unique = results_last_ditch.filter((row, index, self) => self.findIndex(r => r.productId === row.productId) === index);
        curations = createCurations(results_last_ditch_unique, [], results_last_ditch_mapped);
    }
    curations.sort((a, b) => a.closeness - b.closeness);
    const best_curations = curations.slice(0, number_of_curations);
    if (!best_curations.length) {
        failReason = "No products within price point";
        return failReason;
    }
    return best_curations[0].curation.map(curation_item => curation_item.id.split("/").pop());
};
export { generateCuration, customFilterSelectOption };
// Add this at the bottom of your file
const wfTools = { generateCuration };
export default wfTools;
